import { lazy, Suspense } from "react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { ArrowRight, ArrowDown, Users } from "lucide-react";

// three.js (~700KB) loads after first paint, only on the landing page
const HeroScene = lazy(() => import("./HeroScene"));

const lineVariants = {
  hidden: { y: "115%" },
  visible: (i) => ({
    y: 0,
    transition: { duration: 1, ease: [0.22, 1, 0.36, 1], delay: 0.35 + i * 0.15 },
  }),
};

function Layer({ sx, sy, depth, className, children }) {
  const x = useTransform(sx, [0, 1], [-depth, depth]);
  const y = useTransform(sy, [0, 1], [-depth * 0.7, depth * 0.7]);
  return (
    <motion.div className={className} style={{ x, y }}>
      {children}
    </motion.div>
  );
}

export default function Hero() {
  const mx = useMotionValue(0.5);
  const my = useMotionValue(0.5);
  const sx = useSpring(mx, { stiffness: 55, damping: 18 });
  const sy = useSpring(my, { stiffness: 55, damping: 18 });
  const rotX = useTransform(sy, [0, 1], [4, -4]);
  const rotY = useTransform(sx, [0, 1], [-5, 5]);

  const onMouseMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width);
    my.set((e.clientY - r.top) / r.height);
  };

  return (
    <section
      id="top"
      onMouseMove={onMouseMove}
      className="relative min-h-screen overflow-hidden bg-[#5B4199]"
      data-testid="hero-section"
    >

      <div className="absolute inset-y-0 right-0 w-full lg:w-[52%] opacity-25 lg:opacity-100" style={{ perspective: 1400 }}>
        {/* violet halo behind the sculpture - fakes the bloom glow */}
        <div
          aria-hidden="true"
          className="absolute inset-0 pointer-events-none"
          style={{
            background: "radial-gradient(closest-side, rgba(199,178,255,0.55), rgba(157,123,238,0.2) 48%, transparent 72%)",
            backgroundPosition: "center 62%",
            backgroundRepeat: "no-repeat",
            filter: "blur(10px)",
          }}
        />
        <motion.div className="h-full w-full" style={{ rotateX: rotX, rotateY: rotY, transformStyle: "preserve-3d" }}>
          <Suspense fallback={null}>
            <HeroScene />
          </Suspense>
        </motion.div>
        <Layer sx={sx} sy={sy} depth={52} className="absolute top-[22%] right-[6%] hidden lg:block">
          <motion.span
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 1.5, ease: [0.22, 1, 0.36, 1] }}
            className="inline-flex items-center gap-2 rounded-full bg-white shadow-xl px-4 py-2.5 text-xs font-bold text-[#0B132B] animate-float-soft"
            data-testid="hero-pill-0"
          >
            <span className="h-2 w-2 rounded-full bg-[#5B4199]" />
            EOR · live in 2–4 weeks
          </motion.span>
        </Layer>
        <Layer sx={sx} sy={sy} depth={64} className="absolute bottom-[30%] right-[26%] hidden lg:block">
          <motion.span
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 1.65, ease: [0.22, 1, 0.36, 1] }}
            className="inline-flex items-center gap-2 rounded-full bg-[#0B132B] shadow-xl px-4 py-2.5 text-xs font-bold text-white animate-float-soft"
            style={{ animationDelay: "1.6s" }}
            data-testid="hero-pill-1"
          >
            <Users className="h-3.5 w-3.5 text-[#A78BFA]" />
            100% IP ownership
          </motion.span>
        </Layer>
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full min-h-screen flex flex-col justify-end pb-20 pt-40">
        <div className="max-w-3xl">
          <motion.span
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.15 }}
            className="inline-flex items-center rounded-full border border-white/40 px-4 py-1.5 text-[11px] font-semibold uppercase tracking-[0.16em] text-white mb-8"
            data-testid="hero-eyebrow"
          >
            India capability, built around your business
          </motion.span>

          <h1 className="font-display text-[13vw] sm:text-7xl lg:text-[96px] font-extrabold tracking-tight leading-[0.98] text-white">
            <span className="masked-line">
              <motion.span custom={0} variants={lineVariants} initial="hidden" animate="visible">
                Build in India.
              </motion.span>
            </span>
            <span className="masked-line">
              <motion.span custom={1} variants={lineVariants} initial="hidden" animate="visible">
                Stay in control.
              </motion.span>
            </span>
          </h1>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.95, ease: [0.22, 1, 0.36, 1] }}
          >
            <p className="mt-8 max-w-xl text-base sm:text-lg text-white/85 leading-relaxed" data-testid="hero-subcopy">
              Launch and scale your India operation through a fully managed ODC or GCC model.
              Expante provides the people, infrastructure, operations, and local expertise - while
              you retain control of the team, culture, and direction.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <a
                href="#contact"
                data-testid="hero-primary-cta"
                className="group inline-flex items-center gap-2 bg-white hover:bg-[#0B132B] text-[#0B132B] hover:text-white font-semibold rounded-full px-7 py-3.5 transition-colors duration-300 shadow-[0_20px_45px_-15px_rgba(11,19,43,0.45)]"
              >
                Discuss your India strategy
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5" />
              </a>
              <a
                href="#models"
                data-testid="hero-secondary-cta"
                className="group inline-flex items-center gap-2 border-2 border-white/40 hover:border-white text-white font-semibold rounded-full px-7 py-3.5 transition-colors duration-300"
              >
                Compare models
                <ArrowDown className="h-4 w-4 transition-transform duration-300 group-hover:translate-y-0.5" />
              </a>
            </div>
          </motion.div>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2, duration: 1 }}
        aria-hidden="true"
        className="absolute bottom-6 left-4 sm:left-6 lg:left-8 hidden lg:flex items-center gap-2 rounded-full border border-white/25 px-3.5 py-2"
      >
        <span className="h-1.5 w-1.5 rounded-full bg-white animate-pulse" />
        <span className="text-[10px] font-mono-tech uppercase tracking-[0.28em] text-white/70">Scroll</span>
      </motion.div>
    </section>
  );
}
